import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "home-echarts-header" }
const _hoisted_2 = { class: "home-echarts-header__title" }
const _hoisted_3 = { class: "home-echarts-header__controls" }

import { ref, reactive, onBeforeMount, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useECharts } from '@/hooks/useECharts'
import { getCurrentMonthDateRange, toBeijingTime } from '@/utils/timeFormat'
import { useInteractionStore } from '@/stores/interaction'
import { sourceList as sourceListFbu, userList as userListFbu } from '@/api/gucangSales/index'
import { sourceList as sourceListOfbu, userList as userListOfbu } from '@/api/overseasSales/index'
import { sourceList as sourceListLbu, userList as userListLbu } from '@/api/yuntuSales/index'
import type { Ref, PropType } from 'vue'

const __default__ = { name: 'HomePersonBarChar' }

interface QueryFormType {
  depCode: string
  startTime: string
  endTime: string
}
interface ItemCommonType {
  xLabel: string
  source: string
  userName: string
  customerCount: number
  clueCount: number
  chanceCount: number
  followCount: number
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  title: {
    type: String as PropType<string>
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_ofbu' | 'cos_lbu'>,
    required: true
  },
  type: {
    type: String as PropType<'lead' | 'user'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要转换时区
  isTimeZoneConv: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要支持多语言
  isMultiLanguage: {
    type: Boolean as PropType<boolean>,
    default: false
  }
},
  setup(__props) {

const { t } = useI18n()
const { language } = storeToRefs(useInteractionStore())
const { firstDayOfMonth, currentDayOfMonth } = getCurrentMonthDateRange()

const props = __props

const title = computed(() => {
  const i18nTitleEnum = { lead: t('home.k51tch'), user: t('home.hv78t7') }
  const titleEnum = { lead: '线索来源统计', user: '按负责人统计' }
  return props.title || props.isMultiLanguage ? i18nTitleEnum[props.type] : titleEnum[props.type]
})
// 函数枚举
const leadUserEnum = {
  cos_fbu: { lead: sourceListFbu, user: userListFbu },
  cos_ofbu: { lead: sourceListOfbu, user: userListOfbu },
  cos_lbu: { lead: sourceListLbu, user: userListLbu }
}
const spinning = ref(false)
const showECharts = ref(false)
const queryForm = reactive<QueryFormType>({
  depCode: 'all',
  startTime: firstDayOfMonth,
  endTime: currentDayOfMonth
})
const getData = () => {
  const params = Object.assign({}, queryForm)
  if (props.isTimeZoneConv) {
    params.startTime = toBeijingTime(params.startTime)
    params.endTime = toBeijingTime(params.endTime)
  }
  const fn = leadUserEnum[props.appId][props.type]
  fn(params)
    .then((res) => {
      const data = res.data as unknown as ItemCommonType[]
      if (data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        showECharts.value = true
        const keyEnum = { lead: 'source', user: 'userName' }
        data.forEach((item) => (item.xLabel = item[keyEnum[props.type]]))
        initChart(data)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemCommonType[]) => {
  setOptions({
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: [t('home.2elm35'), t('home.q9wxf8'), t('home.4z5hih'), t('home.9onhsr')],
      right: '50px'
    },
    toolbox: {
      feature: {
        magicType: {
          show: false,
          type: ['line', 'bar']
        }
      }
    },
    grid: {
      left: '80',
      right: '50',
      bottom: '50'
    },
    calculable: false,
    xAxis: [
      {
        type: 'category',
        data: data.map((item) => item.xLabel),
        axisLabel: {
          // 倾斜的程度
          formatter: function (value: any) {
            if (value.length > 7) {
              return `${value.slice(0, 7)}...`
            }
            return value
          }
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          // 倾斜的程度
          margin: 2,
          formatter: function (value: any) {
            // 如果是谷仓海外销售，不需要转换
            if (props.appId === 'cos_ofbu') return value

            if (value >= 10000 && value < 100000000) {
              value = value / 10000 + '万'
            } else if (value >= 100000000 && value < 1000000000000) {
              value = value / 100000000 + '亿'
            } else if (value >= 1000000000000) {
              value = value / 1000000000000 + '兆'
            }
            return value
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        name: t('home.2elm35'),
        type: 'bar',
        itemStyle: {
          color: '#3F97FF'
        },
        data: data.map((item) => item.customerCount)
      },
      {
        name: t('home.q9wxf8'),
        type: 'bar',
        itemStyle: {
          color: '#877CED'
        },
        data: data.map((item) => item.clueCount)
      },
      {
        name: t('home.4z5hih'),
        type: 'bar',
        itemStyle: {
          color: '#36C8E1'
        },
        data: data.map((item) => item.chanceCount)
      },
      {
        name: t('home.9onhsr'),
        type: 'bar',
        itemStyle: {
          color: '#FFB648'
        },
        data: data.map((item) => item.followCount)
      }
    ]
  })
}
onBeforeMount(() => getData())
if (props.isMultiLanguage) {
  watch(
    () => language.value,
    () => getData()
  )
}

return (_ctx: any,_cache: any) => {
  const _component_CosRangePicker = _resolveComponent("CosRangePicker")!
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_CosSelect = _resolveComponent("CosSelect")!
  const _component_dc_layout_empty = _resolveComponent("dc-layout-empty")!
  const _component_a_spin = _resolveComponent("a-spin")!

  return (_openBlock(), _createElementBlock("div", {
    class: "home-echarts-wrap",
    style: _normalizeStyle({ width: __props.width, height: __props.height })
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h3", _hoisted_2, _toDisplayString(title.value), 1),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_CosRangePicker, {
          startTime: queryForm.startTime,
          "onUpdate:startTime": _cache[0] || (_cache[0] = ($event: any) => ((queryForm.startTime) = $event)),
          endTime: queryForm.endTime,
          "onUpdate:endTime": _cache[1] || (_cache[1] = ($event: any) => ((queryForm.endTime) = $event)),
          allowClear: props.allowClear,
          onChange: getData
        }, null, 8, ["startTime", "endTime", "allowClear"]),
        _createVNode(_component_CosSelect, {
          value: queryForm.depCode,
          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((queryForm.depCode) = $event)),
          allowClear: props.allowClear,
          style: {"width":"145px","margin-left":"10px"},
          onChange: getData
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_select_option, { value: "all" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(props.isMultiLanguage ? _unref(t)('home.jio30c') : '全部数据'), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_a_select_option, { value: "my" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(props.isMultiLanguage ? _unref(t)('home.dd13al') : '我的数据'), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["value", "allowClear"])
      ])
    ]),
    _createVNode(_component_a_spin, {
      spinning: spinning.value,
      delay: 200
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", {
          ref_key: "chartRef",
          ref: chartRef,
          class: "home-echarts-content"
        }, null, 512), [
          [_vShow, showECharts.value]
        ]),
        (!showECharts.value)
          ? (_openBlock(), _createBlock(_component_dc_layout_empty, {
              key: 0,
              type: "nothing"
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["spinning"])
  ], 4))
}
}

})